/**
 * Authentication API service
 * Handles API requests related to authentication
 */
import { apiClient } from './client';
import createLogger from '../utils/logger';

const logger = createLogger('authService');

/**
 * Login user with username/email and password
 * @param {string} identifier - User's username or email
 * @param {string} password - User's password
 * @returns {Promise<Object>} Response data
 */
export const login = async (identifier, password) => {
  try {
    logger.debug('Attempting login with identifier:', identifier);
    // Send both email and username fields, backend will handle which one to use
    const response = await apiClient.post('/auth/login', { 
      email: identifier, // For backward compatibility
      username: identifier, // For username login
      password 
    });
    // Login successful
    return response.data;
  } catch (error) {
    logger.error('Login error:', error);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      throw error.response.data || { message: `Login failed: ${error.response.status}` };
    } else if (error.request) {
      // The request was made but no response was received
      throw { message: 'No response from server. Please check your connection.' };
    } else {
      // Something happened in setting up the request that triggered an Error
      throw { message: `Login request failed: ${error.message}` };
    }
  }
};

/**
 * Logout user by clearing token
 * @returns {Promise<Object>} Response data
 */
export const logout = async () => {
  try {
    logger.debug('Logging out user');
    const response = await apiClient.post('/auth/logout');
    // Logout successful
    return response.data;
  } catch (error) {
    logger.error('Logout error:', error);
    if (error.response) {
      throw error.response.data || { message: `Logout failed: ${error.response.status}` };
    } else if (error.request) {
      throw { message: 'No response from server. Please check your connection.' };
    } else {
      throw { message: `Logout request failed: ${error.message}` };
    }
  }
};

/**
 * Verify if user is authenticated
 * @returns {Promise<Object>} Response data with authentication status
 */
export const verifyAuth = async () => {
  try {
    // Verifying authentication
    const response = await apiClient.get('/auth/verify');
    // Verification successful
    
    // The backend returns { authenticated: true, user: {...} }
    if (response.data.authenticated) {
      return { 
        authenticated: true, 
        user: response.data.user 
      };
    } else {
      return { 
        authenticated: false, 
        error: response.data.message || 'Not authenticated' 
      };
    }
  } catch (error) {
    // Don't log 401 errors as they are expected when user is not authenticated
    if (error.response && error.response.status === 401) {
      return { authenticated: false, error: 'Not authenticated' };
    }
    
    // Log other errors as they are unexpected
    logger.error('Auth verification error:', error);
    
    // For verification, we don't want to throw errors as this is often called
    // on app initialization. Instead, return a structured error response.
    if (error.response) {
      return { authenticated: false, error: `Verification failed: ${error.response.status}` };
    } else if (error.request) {
      return { authenticated: false, error: 'No response from server' };
    } else {
      return { authenticated: false, error: `Request failed: ${error.message}` };
    }
  }
};

/**
 * Configure axios interceptors for authentication
 * This adds token handling and error processing
 */
export default {
  login,
  logout,
  verifyAuth
};
